/**
 * @fileoverview Pixel Talent Monetization Form
 * Client-facing form that displays dynamic Terms of Service based on 
 * the monetization/management split percentages selected by the team.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import { getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import '../SnapMonetization/SnapMonetizationForm.css';


/**
 * Pixel Monetization Form component for creator onboarding.
 * Features:
 * - Dynamic TOS based on selected split percentages
 * - Comprehensive form validation
 * - File upload for verification video
 * - Authentication integration
 * 
 * @returns {React.Element} Rendered PixelMonetizationForm component
 */
function PixelMonetizationForm() {
  const navigate = useNavigate();
  const { isAuthenticated, getAccessToken } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    verification_status: '',
    snap_username: '',
    legal_name: '',
    email_for_snapchat: '',
    phone: '',
    preferred_contact_method: '',
    country: '',
    city: '',
    content_category: '',
    instagram_link: '',
    tiktok_link: '',
    youtube_link: '',
    press_links: '',
    verification_video: null,
    profile_photo: null
  });
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [existingResponse, setExistingResponse] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Split percentages from team selection
  const [monetizationSplit, setMonetizationSplit] = useState(null);
  const [managementSplit, setManagementSplit] = useState(null);
  const [splitSelectionPending, setSplitSelectionPending] = useState(false);
  
  // TOS content fetched from database
  const [tosContent, setTosContent] = useState(null);
  const [tosLoading, setTosLoading] = useState(false);
  
  // Spotlight opt-in
  const [spotlightOptIn, setSpotlightOptIn] = useState(null);
  const [spotlightTiktokUsername, setSpotlightTiktokUsername] = useState('');
  
  // Admin access
  const [adminAdded, setAdminAdded] = useState(null); // true = "I Added You", false = "Not Available"
  
  // Partner/referrer info (for special TOS like Unleash, Wav)
  const [referredByName, setReferredByName] = useState('');
  
  // Refs
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const formRef = useRef(null);
  
  // Token handling for iOS app integration
  const [iosTokenRef, setIosTokenRef] = useState(null);

  /**
   * Get auth headers for API calls
   */
  const getAuthHeaders = useCallback(async () => {
    try {
      if (iosTokenRef) {
        return { headers: { Authorization: `Bearer ${iosTokenRef}` } };
      }
      
      // Try to get token with retry logic
      let token = await getAccessToken();
      
      // If first attempt fails, try refreshing the session
      if (!token) {
        console.log('[PIXEL MONETIZATION] First token fetch failed, attempting to refresh session...');
        try {
          const { tokens } = await fetchAuthSession({ forceRefresh: true });
          if (tokens?.idToken) {
            token = tokens.idToken.toString();
            console.log('[PIXEL MONETIZATION] Session refreshed successfully');
          }
        } catch (refreshError) {
          console.error('[PIXEL MONETIZATION] Session refresh failed:', refreshError);
        }
      }
      
      if (!token) return null;
      return { headers: { Authorization: `Bearer ${token}` } };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      return null;
    }
  }, [getAccessToken, iosTokenRef]);

  /**
   * Initialize the component
   */
  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(window.location.search);
      const iosToken = params.get('token');
      
      if (iosToken) {
        try {
          const tokenParts = iosToken.split('.');
          const payload = JSON.parse(atob(tokenParts[1]));
          const userId = payload.username || payload.sub;
          
          if (!userId) {
            navigate('/survey-login');
            return;
          }

          const userInfoData = {
            username: userId,
            email: userId,
            sub: userId,
            client_id: userId
          };
          
          setUserInfo(userInfoData);
          setIosTokenRef(iosToken);
          await loadClientInfo(userId, iosToken);
          await loadExistingResponse(iosToken);
          return;
          
        } catch (error) {
          console.error('iOS token decode failed:', error);
          navigate('/survey-login');
          return;
        }
      } else if (!isAuthenticated) {
        navigate('/survey-login?redirect=/pixel-monetization');
        return;
      }
      
      try {
        const { username, userId, signInDetails } = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        
        const userInfoData = {
          username,
          email: signInDetails.loginId,
          sub: userId,
          client_id: userAttributes['custom:ClientID'] || userAttributes['custom:UserID'] || userId
        };
        
        setUserInfo(userInfoData);
        setFormData(prev => ({
          ...prev,
          email: signInDetails.loginId || ''
        }));
        
        await loadClientInfo(userInfoData.client_id);
        await loadExistingResponse();
        
      } catch (error) {
        console.error('Error initializing form:', error);
        navigate('/survey-login');
      }
    };

    init();
  }, [isAuthenticated, navigate]);

  /**
   * Restore form data from localStorage if available
   * This helps recover data if the user's session expired during form filling
   */
  useEffect(() => {
    try {
      const backup = localStorage.getItem('pixelMonetizationFormBackup');
      if (backup) {
        const parsed = JSON.parse(backup);
        const backupAge = new Date() - new Date(parsed.timestamp);
        
        // Only restore if backup is less than 1 hour old
        if (backupAge < 3600000) {
          console.log('[PIXEL MONETIZATION] Restoring form data from backup');
          
          // Only restore if we don't have existing response loaded
          if (!existingResponse && parsed.formData) {
            setFormData(prev => ({ ...prev, ...parsed.formData }));
          }
          
          if (parsed.termsAccepted !== undefined) setTermsAccepted(parsed.termsAccepted);
          if (parsed.monetizationSplit !== undefined) setMonetizationSplit(parsed.monetizationSplit);
          if (parsed.managementSplit !== undefined) setManagementSplit(parsed.managementSplit);
          if (parsed.spotlightOptIn !== undefined) setSpotlightOptIn(parsed.spotlightOptIn);
          if (parsed.spotlightTiktokUsername) setSpotlightTiktokUsername(parsed.spotlightTiktokUsername);
          if (parsed.adminAdded !== undefined) setAdminAdded(parsed.adminAdded);
          
          // Show a message to user
          setError('Your previous form data has been restored. You can continue where you left off.');
          setTimeout(() => setError(null), 5000);
        } else {
          // Clear old backup
          localStorage.removeItem('pixelMonetizationFormBackup');
        }
      }
    } catch (error) {
      console.error('Error restoring form backup:', error);
    }
  }, [existingResponse]);

  /**
   * Fetch TOS content from database based on split selection
   * Builds the template key based on type and percentage
   */
  const fetchTosContent = async (monSplit, mgmtSplit) => {
    try {
      setTosLoading(true);
      
      // Build the template key based on what splits are set
      let templateKey;
      if (monSplit && !mgmtSplit) {
        // Monetization only
        templateKey = `monetization-${monSplit}`;
      } else if (!monSplit && mgmtSplit) {
        // Management only
        templateKey = `management-${mgmtSplit}`;
      } else if (monSplit && mgmtSplit) {
        // Both - this would be monetization + addon
        // For now, fetch monetization template
        templateKey = `monetization-${monSplit}`;
      }
      
      if (!templateKey) {
        console.log('[PIXEL MONETIZATION] No template key to fetch');
        setTosLoading(false);
        return;
      }
      
      console.log('[PIXEL MONETIZATION] Fetching TOS template:', templateKey);
      const response = await axios.get(API_ENDPOINTS.TOS_TEMPLATES.GET(templateKey));
      setTosContent(response.data.content);
      console.log('[PIXEL MONETIZATION] Fetched TOS content for', templateKey);
    } catch (error) {
      console.error('Error fetching TOS content:', error);
      // TOS content will be null, form will show a message
    } finally {
      setTosLoading(false);
    }
  };

  /**
   * Load client information including split selection
   */
  const loadClientInfo = async (clientId, token = null) => {
    try {
      let authHeaders;
      if (token) {
        authHeaders = { headers: { Authorization: `Bearer ${token}` } };
      } else {
        authHeaders = await getAuthHeaders();
        if (!authHeaders) return;
      }

      const response = await axios.get(API_ENDPOINTS.LEADS.CLIENT_WITH_PARTNER(clientId), authHeaders);
      const clientData = response.data;
      
      console.log('[PIXEL MONETIZATION] Client data:', clientData);
      
      // Check for split selections from onboard_automation ONLY
      // The backend merges onboard_automation splits into clientData
      // We should ONLY use these authoritative values, not the ClientInfo fields
      let monSplit = null;
      let mgmtSplit = null;
      
      // ONLY check monetization_split from onboard_automation (ignore selected_monetization_split from ClientInfo)
      if (clientData.monetization_split !== undefined && clientData.monetization_split !== null) {
        monSplit = clientData.monetization_split;
        setMonetizationSplit(monSplit);
        console.log('[PIXEL MONETIZATION] Monetization split:', monSplit);
      }
      
      // ONLY check management_split from onboard_automation (ignore selected_management_split from ClientInfo)
      if (clientData.management_split !== undefined && clientData.management_split !== null) {
        mgmtSplit = clientData.management_split;
        setManagementSplit(mgmtSplit);
        console.log('[PIXEL MONETIZATION] Management split:', mgmtSplit);
      }
      
      // Get partner/referrer name for special TOS handling
      if (clientData.referred_by_name) {
        setReferredByName(clientData.referred_by_name);
        console.log('[PIXEL MONETIZATION] Referred by:', clientData.referred_by_name);
      } else {
        console.warn('[PIXEL MONETIZATION] WARNING: referred_by_name is empty/null/undefined');
        console.log('[PIXEL MONETIZATION] Full clientData:', JSON.stringify(clientData, null, 2));
      }
      
      // Fetch TOS content if splits are selected (only for non-Unleash/Wav clients)
      const isSpecialPartner = clientData.referred_by_name && (
        clientData.referred_by_name.toLowerCase() === 'unleash' ||
        clientData.referred_by_name.toLowerCase() === 'wav' ||
        clientData.referred_by_name.toLowerCase() === 'we are verified'
      );
      
      if ((monSplit || mgmtSplit) && !isSpecialPartner) {
        await fetchTosContent(monSplit, mgmtSplit);
      }
      
      // Check if split selection is still pending
      if (clientData.pending_split_selection?.status === 'pending') {
        setSplitSelectionPending(true);
      }
      
      // Filter out placeholder phone numbers (0000000000 or similar)
      const phoneValue = clientData.Phone || '';
      const isPlaceholderPhone = /^0+$/.test(phoneValue.replace(/\D/g, ''));
      
      // Auto-fill form
      setFormData(prev => ({
        ...prev,
        email: clientData.Email_Address || prev.email,
        legal_name: `${clientData.First_Legal_Name || ''} ${clientData.Last_Legal_Name || ''}`.trim() || prev.legal_name,
        snap_username: clientData.Snap_Username || prev.snap_username,
        phone: isPlaceholderPhone ? prev.phone : phoneValue,
        preferred_contact_method: clientData.Preferred_Contact_Method || prev.preferred_contact_method,
        instagram_link: clientData.IG_Username ? `https://instagram.com/${clientData.IG_Username}` : prev.instagram_link,
        tiktok_link: clientData.TT_Username ? `https://tiktok.com/@${clientData.TT_Username}` : prev.tiktok_link,
        youtube_link: clientData.YT_Username ? `https://youtube.com/@${clientData.YT_Username}` : prev.youtube_link,
        verification_status: (() => {
          const isVerified = clientData.IG_Verified || clientData.TT_Verified || clientData.YT_Verified || clientData.Snap_Star;
          const isMonetized = clientData.Snap_Monetized;
          if (isVerified && isMonetized) return 'Both';
          if (isVerified) return 'Verified';
          if (isMonetized) return 'Monetized';
          return prev.verification_status;
        })()
      }));
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading client info:', error);
      setLoading(false);
    }
  };

  /**
   * Load existing form response
   */
  const loadExistingResponse = async (token = null) => {
    try {
      let authHeaders;
      if (token) {
        authHeaders = { headers: { Authorization: `Bearer ${token}` } };
      } else {
        authHeaders = await getAuthHeaders();
        if (!authHeaders) return;
      }

      const response = await axios.get(API_ENDPOINTS.PIXEL_MONETIZATION?.MY_RESPONSE || '/api/pixel-monetization/my-response', authHeaders);
      
      if (response.data) {
        setExistingResponse(response.data);
        setIsEditing(true);
        
        // Filter out placeholder phone numbers (0000000000 or similar)
        const phoneValue = response.data.phone || '';
        const isPlaceholderPhone = /^0+$/.test(phoneValue.replace(/\D/g, ''));
        
        setFormData(prev => ({
          ...prev,
          email: response.data.email || prev.email,
          verification_status: response.data.verification_status || '',
          snap_username: response.data.snap_username || '',
          legal_name: response.data.legal_name || '',
          email_for_snapchat: response.data.email_for_snapchat || '',
          phone: isPlaceholderPhone ? '' : phoneValue,
          preferred_contact_method: response.data.preferred_contact_method || '',
          country: response.data.country || '',
          city: response.data.city || '',
          content_category: response.data.content_category || '',
          instagram_link: response.data.instagram_link || '',
          tiktok_link: response.data.tiktok_link || '',
          youtube_link: response.data.youtube_link || '',
          press_links: response.data.press_links || ''
        }));
        
        if (response.data.terms_accepted !== undefined) {
          setTermsAccepted(response.data.terms_accepted);
        }
      }
    } catch (error) {
      // No existing response is fine
      console.log('No existing response found');
    }
  };

  /**
   * Handle form field changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (invalidFields.includes(name)) {
      setInvalidFields(prev => prev.filter(f => f !== name));
    }
  };

  /**
   * Handle file upload
   */
  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, [fieldName]: file }));
      if (invalidFields.includes(fieldName)) {
        setInvalidFields(prev => prev.filter(f => f !== fieldName));
      }
    }
  };

  /**
   * Validate form before submission
   */
  const validateForm = () => {
    const required = ['email', 'verification_status', 'snap_username', 'legal_name', 'email_for_snapchat', 'phone', 'country', 'city', 'content_category', 'instagram_link', 'tiktok_link'];
    const invalid = [];
    
    for (const field of required) {
      if (!formData[field]?.trim()) {
        invalid.push(field);
      }
    }
    
    // Validate phone number
    if (formData.phone) {
      const digitsOnly = formData.phone.replace(/\D/g, '');
      // Check if all zeros
      if (/^0+$/.test(digitsOnly)) {
        invalid.push('phone');
        setError('Please enter a valid phone number (not all zeros)');
      }
      // Check minimum length (at least 7 digits for a valid phone)
      else if (digitsOnly.length < 7) {
        invalid.push('phone');
        setError('Phone number must have at least 7 digits');
      }
      // Check maximum length (no more than 15 digits per E.164 standard)
      else if (digitsOnly.length > 15) {
        invalid.push('phone');
        setError('Phone number is too long');
      }
    }
    
    // Require verification video for new submissions
    if (!isEditing && !formData.verification_video) {
      invalid.push('verification_video');
    }
    
    // Check if this is a special partner (Unleash/Wav) that always requires TOS
    const isSpecialPartner = referredByName && (
      referredByName.toLowerCase() === 'unleash' ||
      referredByName.toLowerCase() === 'wav' ||
      referredByName.toLowerCase() === 'we are verified'
    );
    
    // Check if this is a management-only client (Wav management wave)
    const MANAGEMENT_ONLY_EMAILS = [
      'gibz@weareverified.co',
      'peytoncoffee@weareverified.co',
      'samanthajo@weareverified.co',
      'sophmosca@weareverified.co',
      'team@trevimoran.com'
    ];
    const MANAGEMENT_ONLY_CLIENT_IDS = [
      'g69104876', 'pc39110588', 'sj05318225', 'sm78177163', 'tm05079653'
    ];
    const userEmail = formData.email?.toLowerCase() || '';
    const userClientId = userInfo?.client_id || '';
    const isManagementOnlyClient = MANAGEMENT_ONLY_EMAILS.includes(userEmail) || 
                                    MANAGEMENT_ONLY_CLIENT_IDS.includes(userClientId);
    
    // Require terms acceptance if splits are selected OR if special partner OR if management-only client
    if ((monetizationSplit || isSpecialPartner || isManagementOnlyClient) && !termsAccepted) {
      setError('You must accept the Terms of Service Agreement to continue');
      invalid.push('terms');
    }
    
    // Require TikTok username if Spotlight opt-in is yes
    if (spotlightOptIn === true && !spotlightTiktokUsername.trim()) {
      invalid.push('spotlight_tiktok_username');
    }
    
    setInvalidFields(invalid);
    return invalid.length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    // Save form data to localStorage as backup
    try {
      localStorage.setItem('pixelMonetizationFormBackup', JSON.stringify({
        formData,
        termsAccepted,
        monetizationSplit,
        managementSplit,
        spotlightOptIn,
        spotlightTiktokUsername,
        adminAdded,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {
      console.warn('Could not save form backup:', e);
    }
    
    try {
      const authHeaders = await getAuthHeaders();
      if (!authHeaders) {
        setError('Your session has expired. Please refresh the page to continue. Your form data has been saved and will be restored when you return.');
        setSubmitting(false);
        return;
      }

      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          submitData.append(key, value);
        }
      });
      
      submitData.append('terms_accepted', termsAccepted);
      submitData.append('monetization_split', monetizationSplit || '');
      submitData.append('management_split', managementSplit || '');
      submitData.append('spotlight_opt_in', spotlightOptIn === true ? 'true' : 'false');
      if (spotlightOptIn === true) {
        submitData.append('spotlight_tiktok_username', spotlightTiktokUsername.trim());
      }
      submitData.append('admin_added', adminAdded === true ? 'true' : 'false');

      setUploadStatus('Uploading...');
      
      const endpoint = isEditing 
        ? (API_ENDPOINTS.PIXEL_MONETIZATION?.UPDATE || '/api/snap-monetization/update')
        : (API_ENDPOINTS.PIXEL_MONETIZATION?.SUBMIT || '/api/snap-monetization/submit');
      
      // Use PUT for updates, POST for new submissions
      const axiosMethod = isEditing ? axios.put : axios.post;
      
      await axiosMethod(endpoint, submitData, {
        ...authHeaders,
        headers: {
          ...authHeaders.headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Clear backup after successful submission
      try {
        localStorage.removeItem('pixelMonetizationFormBackup');
        console.log('[PIXEL MONETIZATION] Form backup cleared after successful submission');
      } catch (e) {
        console.warn('Could not clear form backup:', e);
      }
      
      setUploadStatus('✓ Form submitted successfully!');
      setSuccess(true);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(error.response?.data?.detail || 'Failed to submit form. Please try again.');
      setUploadStatus('');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Render the Terms of Service section
   * Special handling for Unleash and Wav partners with hardcoded TOS
   * Also handles special management-only clients (Wav management wave)
   * Otherwise fetches from database based on split selection
   */
  const renderTermsOfService = () => {
    // Debug logging
    console.log('[PIXEL MONETIZATION TOS] referredByName:', referredByName);
    console.log('[PIXEL MONETIZATION TOS] monetizationSplit:', monetizationSplit);
    console.log('[PIXEL MONETIZATION TOS] managementSplit:', managementSplit);
    console.log('[PIXEL MONETIZATION TOS] userInfo:', userInfo);
    
    // Special management-only clients (Wav management wave)
    // These clients get a management-only agreement with 30% commission
    const MANAGEMENT_ONLY_EMAILS = [
      'gibz@weareverified.co',
      'peytoncoffee@weareverified.co',
      'samanthajo@weareverified.co',
      'sophmosca@weareverified.co',
      'team@trevimoran.com'
    ];
    const MANAGEMENT_ONLY_CLIENT_IDS = [
      'g69104876',    // Gabby Depietro
      'pc39110588',   // Peyton Coffee
      'sj05318225',   // Samantha Jo
      'sm78177163',   // Soph Mosca
      'tm05079653'    // Trevi Moran
    ];
    
    // Check if current user is in the hardcoded management-only list (5 specific Wav people)
    const userEmail = formData.email?.toLowerCase() || userInfo?.email?.toLowerCase() || '';
    const userClientId = userInfo?.client_id || '';
    const isInManagementOnlyList = MANAGEMENT_ONLY_EMAILS.includes(userEmail) || 
                                    MANAGEMENT_ONLY_CLIENT_IDS.includes(userClientId);
    
    console.log('[PIXEL MONETIZATION TOS] isInManagementOnlyList:', isInManagementOnlyList, 'email:', userEmail, 'clientId:', userClientId);
    
    // Check for special partner TOS (Unleash, Wav)
    const isUnleash = referredByName && referredByName.toLowerCase() === 'unleash';
    const isWav = referredByName && (referredByName.toLowerCase() === 'wav' || referredByName.toLowerCase() === 'we are verified');
    
    console.log('[PIXEL MONETIZATION TOS] isUnleash:', isUnleash, 'isWav:', isWav);
    
    // For the 5 specific Wav management-only clients (hardcoded list only)
    // Other management-only clients (like Jennifer Tran) will use database templates below
    if (isInManagementOnlyList) {
      return (
        <div className="snapped-call-form__section">
          <h3 className="snapped-call-form__section-heading">Terms of Service Agreement</h3>
          <div style={{ 
            padding: '1.5rem', 
            backgroundColor: '#f8f9fa', 
            border: '2px solid #dee2e6', 
            borderRadius: '8px',
            marginBottom: '1rem',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            <div id="management-only-tos-text" style={{ 
              fontSize: '0.9rem', 
              lineHeight: '1.6',
              marginBottom: '1rem'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem', textAlign: 'center' }}>PLATFORM MANAGEMENT SERVICES AGREEMENT</h4>
              
              <p>This agreement ("Agreement") is made and entered into as of the signature date outlined below ("Effective Date"), by and between Pixel ("Company") on the one hand, and the talent outlined in the signature field below ("Talent") on the other hand. The parties, after considering and acknowledging the mutual promises and valuable considerations detailed herein, hereby agree to be bound by the terms and conditions set forth in this Agreement.</p>
              
              <p><strong>1. RECITALS</strong></p>
              <p>WHEREAS the Company has a proven track record of assisting celebrities in producing, curating, and monetizing content on Snapchat, and the Talent is seeking assistance from the Company to grow an audience on Snapchat, leveraging a variety of content sources.</p>
              
              <p><strong>2. MANAGEMENT SERVICES</strong></p>
              
              <p><em>Company Obligations (Management)</em></p>
              <p>Services are as outlined below:</p>
              <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                <li>Work with Talent and their team to aid in the creation of content for Snapchat.</li>
                <li>In some cases, post to Snapchat on the Talent's behalf. If the Company posts on the Talent's behalf, the Talent releases all liability around such posting and any potential negative outcomes from such content.</li>
                <li>Create and advise on a content plan using Snapchat best practices.</li>
                <li>When explicitly requested, curate, segment, edit, and post Snapchat content on behalf of Talent.</li>
              </ul>
              
              <p><em>Term (Management Period)</em></p>
              <p>Management shall commence only upon the Talent's completion of Hyperwallet setup in accordance with this Agreement.</p>
              <p>All management services are at will. Meaning if at any point the talent wishes to stop or change the scope of such services, they may with 30 day written notice.</p>
              
              <p><em>Commissions (Management)</em></p>
              <p>If the Company offers and the Talent accepts, the Company shall receive thirty percent (30%) of Talent's gross revenue (monies collected from Snapchat).</p>
              <p>If the Company elects not to provide management services to the Creator at any time during the Term, the Creator may obtain such management services from a third party.</p>
              
              <p><strong>3. PAYMENT TERMS</strong></p>
              <p>Every thirty (30) days, the Talent shall withdraw earnings from Snapchat into Hyperwallet.</p>
              <p>The Talent shall establish the Hyperwallet payment portal using the shared email address provided by the Company for payment visibility.</p>
              <p>The Talent shall have full access to the Hyperwallet payment portal. The Company shall not have any access to the Hyperwallet payment portal. Both parties shall have full access to the shared email address for payment visibility.</p>
              <p>The Talent shall configure Hyperwallet to automatically distribute seventy percent (70%) of funds to the Talent and thirty percent (30%) of funds to the Company on a monthly basis.</p>
              <p>Upon expiration or termination of the Agreement, the Talent shall be solely responsible for removing the Company's percentage allocation from the payment portal, and may update account email address.</p>
              
              <p><strong>4. GENERAL TERMS</strong></p>
              <p><em>Governing Law:</em> This Agreement has been entered into in the State of Wyoming.</p>
              <p><em>Publicity:</em> Talent grants Company the right to use Talent's name and an approved likeness for promotional purposes exclusively on the Company's website and pitch deck.</p>
              <p><em>Arbitration:</em> All disputes shall be submitted to JAMS and resolved exclusively by confidential binding arbitration in Los Angeles County, California.</p>
              <p><em>Severability:</em> If any provision of this Agreement is deemed invalid or unenforceable, the remaining provisions remain in effect. The parties agree to negotiate in good faith to replace invalid terms with enforceable ones reflecting the original intent.</p>
              <p><em>Assignment:</em> The Company may assign this Agreement at its sole discretion, provided all terms remain unchanged.</p>
              <p><em>Technology Use:</em> Talent will use the Company's technology throughout the partnership to manage Snapchat content, data, and posting.</p>
            </div>
            
            <label style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: '1.5',
              padding: '1rem',
              backgroundColor: '#fff',
              border: '2px solid #007bff',
              borderRadius: '6px',
              fontWeight: '600'
            }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                style={{ 
                  marginRight: '0.75rem', 
                  marginTop: '0.25rem',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
                required
              />
              <span>
                I have read, understood, and agree to be bound by the Platform Management Services Agreement terms outlined above. I acknowledge this is a legally binding agreement.
              </span>
            </label>
          </div>
          {!termsAccepted && error && error.includes('Terms of Service') && (
            <span className="snapped-call-form__error-message" style={{ marginTop: '0.5rem', display: 'block' }}>You must accept the Terms of Service Agreement to continue</span>
          )}
        </div>
      );
    }
    
    // For Unleash clients
    if (isUnleash) {
      return (
        <div className="snapped-call-form__section">
          <h3 className="snapped-call-form__section-heading">Terms of Service Agreement</h3>
          <div style={{ 
            padding: '1.5rem', 
            backgroundColor: '#f8f9fa', 
            border: '2px solid #dee2e6', 
            borderRadius: '8px',
            marginBottom: '1rem',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            <div id="unleash-tos-text" style={{ 
              fontSize: '0.9rem', 
              lineHeight: '1.6',
              marginBottom: '1rem'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem', textAlign: 'center' }}>PLATFORM MANAGEMENT SERVICES AGREEMENT</h4>
              
              <p>This agreement ("Agreement") is made and entered into as of the date of acceptance below ("Effective Date"), by and between UNLEASH ("Company") on the one hand, and you, the talent ("Talent") on the other hand. The parties, after considering and acknowledging the mutual promises and valuable considerations detailed herein, hereby agree to be bound by the terms and conditions set forth in this Agreement.</p>
              
              <p><strong>RECITALS</strong></p>
              <p>WHEREAS the Company has a proven track record of assisting celebrities in producing, curating, and monetizing content on Snapchat, and the Talent is seeking assistance from the Company to grow and monetize an audience on Snapchat, leveraging a variety of content sources.</p>
              
              <p><strong>MONETIZATION SERVICES (STRICT & BINDING)</strong></p>
              
              <p><em>Service</em></p>
              <p>The Company will use commercially reasonable efforts to secure Talent's monetization on Snapchat within sixteen (16) weeks of the Effective Date in exchange for a percentage share in Creator earnings as outlined below. In rare circumstances beyond the company's control, Snapchat may deny a user admission to the monetization program.</p>
              
              <p><em>Term (Monetization Period)</em></p>
              <p>The Monetization Period commences on the Effective Date and shall last for eighteen (18) consecutive months ("Monetization Period").</p>
              
              <p><em>Talent Obligations (Monetization)</em></p>
              <p>During the Monetization Period, Talent shall provide sufficient content to support daily posting on Snapchat in accordance with Company guidelines.</p>
              
              <p><em>Commissions (Monetization)</em></p>
              <p>Company shall be entitled to a commission of twenty percent (20%) of Talent's gross revenue (all monies collected from Snapchat on behalf of Talent) for the entire Monetization Period.</p>
              
              <p>The Monetization Period is binding, non-cancellable, and non-terminable. Talent acknowledges and agrees that they may not exit, withdraw, rescind, or otherwise terminate this Monetization Period for any reason, including but not limited to personal circumstances, dissatisfaction with results, or termination of Management Services.</p>
              
              <p><strong>MANAGEMENT SERVICES</strong></p>
              
              <p><em>Company Obligations (Management)</em></p>
              <p>The Company will:</p>
              <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                <li>Work with Talent and their team to aid in the creation of content for Snapchat;</li>
                <li>In some cases, post to Snapchat on the talent's behalf. If the Company posts on the Client's behalf, the Client releases all liability around such posting and any potential negative outcomes around posting such content;</li>
                <li>Create and advise on a content plan using Snapchat best practices;</li>
                <li>When explicitly requested, curate, segment, edit, and post Snapchat content on behalf of Talent.</li>
              </ul>
              
              <p><em>Term (Management Period)</em></p>
              <p>The Management Period commences on the Effective Date and lasts for twenty-four (24) months.</p>
              <p>The Agreement will automatically extend for an additional twelve (12) months unless Talent provides thirty (30) days written notice of termination before the end of the initial Management Period.</p>
              
              <p><em>Commissions (Management)</em></p>
              <p>Company shall receive an additional up to fifteen percent (15%) of Talent's gross revenue (monies collected from Snapchat) during the Management Period. For a total commission ranging from 20-35%. Management services are fluid and can be adjusted by mutual consent throughout the length of the Management term. The initial rate and service structure will be determined after the Talent becomes monetized on Snapchat. But will always be within the ranges set forth herein.</p>
              
              <p><strong>PAYMENT TERMS</strong></p>
              <p>Every thirty (30) days, either the Talent or in some cases the Company will withdraw money from Snapchat.</p>
              <p>The Company will manage the Talent's Payments for the Talent: Both parties will have full access to such payment portals and the Company will collect the funds from Snapchat on the Talent's behalf and distribute those funds to the Talent each month. The Talent will have full access to monthly statements to verify earnings and can stop the Company's access at any point.</p>
              <p>This Agreement serves as a Letter of Direction, authorizing the Company to collect and manage Talent's payments on Snapchat for the duration of this agreement.</p>
              
              <p><strong>GENERAL TERMS</strong></p>
              <p><em>Governing Law:</em> This Agreement has been entered into in the State of Wyoming.</p>
              <p><em>Publicity:</em> Talent grants Company the right to use Talent's name and an approved likeness for promotional purposes exclusively on the Company's website and in its pitch deck.</p>
              <p><em>Arbitration:</em> All disputes shall be submitted to JAMS and resolved exclusively by confidential binding arbitration in Los Angeles County, California.</p>
              <p><em>Severability:</em> If any provision of this Agreement is deemed invalid or unenforceable, the remaining provisions remain in effect. Parties agree to negotiate in good faith to replace invalid terms with enforceable ones reflecting original intent.</p>
              <p><em>Assignment:</em> The Company may assign this Agreement at their sole discretion, provided that all terms remain unchanged.</p>
              <p>Talent will use the Company's technology throughout the partnership to manage Snapchat content, data, and posting.</p>
              
              <p style={{ marginTop: '1.5rem', fontStyle: 'italic', fontWeight: '600' }}>
                <strong>ACKNOWLEDGMENT:</strong> I acknowledge that Snapchat does not charge fees or accept payment for program invitations, and that any third party offering such access in exchange for payment is not authorized to act on Snapchat's behalf. I understand and consent to UNLEASH facilitating my onboarding into Snapchat's creator programs on my behalf under the complete terms outlined above.
              </p>
            </div>
            
            <label style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: '1.5',
              padding: '1rem',
              backgroundColor: '#fff',
              border: '2px solid #007bff',
              borderRadius: '6px',
              fontWeight: '600'
            }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                style={{ 
                  marginRight: '0.75rem', 
                  marginTop: '0.25rem',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
                required
              />
              <span>
                I have read, understood, and agree to be bound by the Platform Management Services Agreement terms outlined above. I acknowledge this is a legally binding agreement.
              </span>
            </label>
          </div>
          {!termsAccepted && error && error.includes('Terms of Service') && (
            <span className="snapped-call-form__error-message">You must accept the Terms of Service Agreement to continue</span>
          )}
        </div>
      );
    }
    
    // For Wav/We Are Verified clients
    if (isWav) {
      return (
        <div className="snapped-call-form__section">
          <h3 className="snapped-call-form__section-heading">Terms of Service Agreement</h3>
          <div style={{ 
            padding: '1.5rem', 
            backgroundColor: '#f8f9fa', 
            border: '2px solid #dee2e6', 
            borderRadius: '8px',
            marginBottom: '1rem',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            <div id="wav-tos-text" style={{ 
              fontSize: '0.9rem', 
              lineHeight: '1.6',
              marginBottom: '1rem'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem', textAlign: 'center' }}>PLATFORM MANAGEMENT SERVICES AGREEMENT</h4>
              
              <p>This agreement ("Agreement") is made and entered into as of the signature date outlined below ("Effective Date"), by and between COMPANY ("Company") on the one hand, and the talent outlined in the signature field below ("Talent") on the other hand. The parties, after considering and acknowledging the mutual promises and valuable considerations detailed herein, hereby agree to be bound by the terms and conditions set forth in this Agreement.</p>
              
              <p><strong>RECITALS</strong></p>
              <p>WHEREAS the Company has a proven track record of assisting celebrities in producing, curating, and monetizing content on Snapchat, and the Talent is seeking assistance from the Company to grow and monetize an audience on Snapchat, leveraging a variety of content sources.</p>
              
              <p><strong>MONETIZATION SERVICES (STRICT &amp; BINDING)</strong></p>
              
              <p><em>Service</em></p>
              <p>The Company will use commercially reasonable efforts to secure Talent's monetization on Snapchat in exchange for a percentage share in Creator earnings as outlined below.</p>
              <p>In rare circumstances beyond the Company's control, Snapchat may deny a user admission to the monetization program.</p>
              
              <p><em>Term (Monetization Period)</em></p>
              <p>The Monetization Period begins on the first day the Creator earns Snapchat crystals (the 'Monetization Start Date') and continues for eighteen (18) consecutive months.</p>
              
              <p><em>Talent Obligations (Monetization)</em></p>
              <p>Provide sufficient content to support daily posting on Snapchat in accordance with Company guidelines.</p>
              
              <p><em>Commissions (Monetization)</em></p>
              <p>Company shall be entitled to a commission of twenty percent (20%) of Talent's gross revenue (all monies collected from Snapchat on behalf of Talent) for the entire Monetization Period.</p>
              <p>The Monetization Period is binding, non-cancellable, and non-terminable. Talent acknowledges and agrees that they may not exit, withdraw, rescind, or otherwise terminate this Monetization Period for any reason, including but not limited to personal circumstances, dissatisfaction with results, or termination of Management Services.</p>
              
              <p><strong>MANAGEMENT SERVICES</strong></p>
              
              <p><em>Company Obligations (Management)</em></p>
              <p>In some cases, after a Talent gets monetized on Snapchat, the Company can provide additional services to such Talent in the form of content management. The cost for these services are in ADDITION to the cost of the Company getting the Talent monetized. If additional services are offered to the Talent, they would be as outlined below:</p>
              <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                <li>Working with Talent and their team to aid in the creation of content for Snapchat.</li>
                <li>Posting to Snapchat on the Talent's behalf. If the Company posts on the Talent's behalf, the Talent releases all liability around such posting and any potential negative outcomes from such content.</li>
                <li>Creating and advising on a content plan using Snapchat best practices.</li>
                <li>When explicitly requested, curating, segmenting, editing, and posting Snapchat content on behalf of Talent.</li>
              </ul>
              
              <p><em>Term (Management Period)</em></p>
              <p>All management services are at will. Meaning if at any point the talent wishes to stop or change the scope of such services, they may with 30 day written notice. Cancelling management services in NO WAY affects the commissions being paid under the monetization term. All monetization terms will remain in tact until the expiration of the 18 month period as outlined above.</p>
              
              <p><em>Commissions (Management)</em></p>
              <p>If the Company offers and the Talent accepts, the Company shall receive an additional up to fifteen percent (15%) of Talent's gross revenue (monies collected from Snapchat) for a total commission ranging from 20–35%. Management services are calculated completely independent of the commission collected in return for getting the Creator monetized.</p>
              <p>The initial rate and service structure will be determined after the Talent becomes monetized on Snapchat but will always remain within the ranges set forth herein.</p>
              <p>If the Company elects not to provide management services to the Creator at any time during the Term, the Creator may obtain such management services from a third party; however, all monetization commissions payable to the Company shall remain unchanged and in full force for the entire duration of the monetization Term, regardless of who provides the management services.</p>
              
              <p><strong>PAYMENT TERMS</strong></p>
              <p>Every thirty (30) days, either the Talent or, in some cases, the Company will withdraw money from Snapchat.</p>
              
              <p>Payment Management</p>
              <p>Both parties will have full access to such payment portals.</p>
              <p>The Company will collect funds from Snapchat on the Talent's behalf and distribute those funds to Talent each month.</p>
              <p>Talent will have full access to monthly statements to verify earnings and may remove the Company's access at any point.</p>
              <p>This Agreement serves as a Letter of Direction, authorizing the Company to collect and manage Talent's payments on Snapchat for the duration of this Agreement.</p>
              
              <p><strong>GENERAL TERMS</strong></p>
              <p>Governing Law: This Agreement has been entered into in the State of Wyoming.</p>
              <p>Publicity: Talent grants Company the right to use Talent's name and an approved likeness for promotional purposes exclusively on the Company's website and pitch deck.</p>
              <p>Arbitration: All disputes shall be submitted to JAMS and resolved exclusively by confidential binding arbitration in Los Angeles County, California.</p>
              <p>Severability: If any provision of this Agreement is deemed invalid or unenforceable, the remaining provisions remain in effect. The parties agree to negotiate in good faith to replace invalid terms with enforceable ones reflecting the original intent.</p>
              <p>Assignment: The Company may assign this Agreement at its sole discretion, provided all terms remain unchanged.</p>
              <p>Technology Use: Talent will use the Company's technology throughout the partnership to manage Snapchat content, data, and posting.</p>
            </div>
            
            <label style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: '1.5',
              padding: '1rem',
              backgroundColor: '#fff',
              border: '2px solid #007bff',
              borderRadius: '6px',
              fontWeight: '600'
            }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                style={{ 
                  marginRight: '0.75rem', 
                  marginTop: '0.25rem',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
                required
              />
              <span>
                I have read, understood, and agree to be bound by the Platform Management Services Agreement terms outlined above. I acknowledge this is a legally binding agreement.
              </span>
            </label>
          </div>
          {!termsAccepted && error && error.includes('Terms of Service') && (
            <span className="snapped-call-form__error-message">You must accept the Terms of Service Agreement to continue</span>
          )}
        </div>
      );
    }
    
    // For all other clients - use database TOS based on split selection
    if (!monetizationSplit && !managementSplit) {
      return null;
    }
    
    if (tosLoading) {
      return (
        <div className="snapped-call-form__section">
          <h3 className="snapped-call-form__section-heading">Terms of Service Agreement</h3>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Loading agreement...</div>
        </div>
      );
    }
    
    const hasMonetization = !!(monetizationSplit && monetizationSplit > 0);
    const hasManagement = !!(managementSplit && managementSplit > 0);
    const totalSplit = (monetizationSplit || 0) + (managementSplit || 0);
    
    return (
      <div className="snapped-call-form__section">
        <h3 className="snapped-call-form__section-heading">Terms of Service Agreement</h3>
        
        <p style={{ marginBottom: '1rem', color: '#555' }}>
          Commission: {hasMonetization && <strong>{monetizationSplit}% Monetization</strong>}
          {hasMonetization && hasManagement && ' + '}
          {hasManagement && <strong>{managementSplit}% Management</strong>}
          {hasMonetization && hasManagement && <> = <strong>{totalSplit}% Total</strong></>}
        </p>

        <div style={{ padding: '1.5rem', backgroundColor: '#f8f9fa', border: '2px solid #dee2e6', borderRadius: '8px', marginBottom: '1rem', maxHeight: '400px', overflowY: 'auto' }}>
          <div style={{ fontSize: '0.9rem', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
            {tosContent || 'Terms of Service content not available. Please contact support.'}
          </div>
        </div>
        
        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '1rem', lineHeight: '1.5', padding: '1rem', backgroundColor: '#fff', border: '2px solid #007bff', borderRadius: '6px', fontWeight: '600' }}>
          <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} style={{ marginRight: '0.75rem', marginTop: '0.25rem', width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0 }} required />
          <span>I have read, understood, and agree to be bound by the Platform Management Services Agreement terms outlined above. I acknowledge this is a legally binding agreement.</span>
        </label>
        
        {!termsAccepted && error && error.includes('Terms of Service') && (
          <span className="snapped-call-form__error-message" style={{ marginTop: '0.5rem', display: 'block' }}>You must accept the Terms of Service Agreement to continue</span>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Split selection pending state
  if (splitSelectionPending && !monetizationSplit) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '24px' }}>⏳</div>
          <h1 className="snapped-call-form__title">Form Not Ready Yet</h1>
          <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '24px' }}>
            Our team is currently reviewing your submission and will prepare your contract terms shortly.
          </p>
          <p style={{ fontSize: '1rem', color: '#888' }}>
            You will receive an email when your form is ready to complete.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>✓</div>
          <h1 className="snapped-call-form__title">Form Submitted Successfully!</h1>
          <p style={{ fontSize: '1.1rem', color: '#666' }}>
            Thank you for completing your Pixel Talent monetization form.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="call-form-page call-form">
      <div className="snapped-call-form__container">
        <h1 className="snapped-call-form__title">Pixel Talent Monetization Form</h1>
        
        {error && (
          <div className="snapped-call-form__error-message" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
            {error}
          </div>
        )}
        
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">Basic Information</h3>
            
            <input
              className={`snapped-call-form__input ${invalidFields.includes('email') ? 'snapped-call-form__input--error' : ''}`}
              type="email"
              name="email"
              placeholder="Email *"
              value={formData.email}
              onChange={handleChange}
              required
            />
            
            <input
              className={`snapped-call-form__input ${invalidFields.includes('legal_name') ? 'snapped-call-form__input--error' : ''}`}
              type="text"
              name="legal_name"
              placeholder="Legal Name *"
              value={formData.legal_name}
              onChange={handleChange}
              required
            />
            
            <input
              className={`snapped-call-form__input ${invalidFields.includes('snap_username') ? 'snapped-call-form__input--error' : ''}`}
              type="text"
              name="snap_username"
              placeholder="Snapchat Username *"
              value={formData.snap_username}
              onChange={handleChange}
              required
            />
            
            <input
              className={`snapped-call-form__input ${invalidFields.includes('email_for_snapchat') ? 'snapped-call-form__input--error' : ''}`}
              type="email"
              name="email_for_snapchat"
              placeholder="Email Used for Snapchat *"
              value={formData.email_for_snapchat}
              onChange={handleChange}
              required
            />
            
            <input
              className={`snapped-call-form__input ${invalidFields.includes('phone') ? 'snapped-call-form__input--error' : ''}`}
              type="tel"
              name="phone"
              placeholder="Phone Number *"
              value={formData.phone}
              onChange={handleChange}
              required
            />
            
            <select
              className={`snapped-call-form__input ${invalidFields.includes('preferred_contact_method') ? 'snapped-call-form__input--error' : ''}`}
              name="preferred_contact_method"
              value={formData.preferred_contact_method}
              onChange={handleChange}
            >
              <option value="">Preferred Contact Method</option>
              <option value="Email">Email</option>
              <option value="Phone">Phone</option>
              <option value="Text">Text</option>
              <option value="WhatsApp">WhatsApp</option>
            </select>
          </div>
          
          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">Location</h3>
            
            <input
              className={`snapped-call-form__input ${invalidFields.includes('country') ? 'snapped-call-form__input--error' : ''}`}
              type="text"
              name="country"
              placeholder="Country *"
              value={formData.country}
              onChange={handleChange}
              required
            />
            
            <input
              className={`snapped-call-form__input ${invalidFields.includes('city') ? 'snapped-call-form__input--error' : ''}`}
              type="text"
              name="city"
              placeholder="City *"
              value={formData.city}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">Content & Verification Status</h3>
            
            <select
              className={`snapped-call-form__input ${invalidFields.includes('verification_status') ? 'snapped-call-form__input--error' : ''}`}
              name="verification_status"
              value={formData.verification_status}
              onChange={handleChange}
              required
            >
              <option value="">Select Snapchat Status *</option>
              <option value="Verified">Verified on Snap</option>
              <option value="Monetized">Monetized on Snap</option>
              <option value="Both">Both</option>
              <option value="Neither">Neither</option>
            </select>
            
            <textarea
              className={`snapped-call-form__input ${invalidFields.includes('content_category') ? 'snapped-call-form__input--error' : ''}`}
              name="content_category"
              placeholder="Describe your content category/niche *"
              value={formData.content_category}
              onChange={handleChange}
              required
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>
          
          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">Social Media Links</h3>
            
            <input
              className={`snapped-call-form__input ${invalidFields.includes('instagram_link') ? 'snapped-call-form__input--error' : ''}`}
              type="url"
              name="instagram_link"
              placeholder="Instagram Profile Link *"
              value={formData.instagram_link}
              onChange={handleChange}
              required
            />
            
            <input
              className={`snapped-call-form__input ${invalidFields.includes('tiktok_link') ? 'snapped-call-form__input--error' : ''}`}
              type="url"
              name="tiktok_link"
              placeholder="TikTok Profile Link *"
              value={formData.tiktok_link}
              onChange={handleChange}
              required
            />
            
            <input
              className="snapped-call-form__input"
              type="url"
              name="youtube_link"
              placeholder="YouTube Channel Link (Optional)"
              value={formData.youtube_link}
              onChange={handleChange}
            />
            
            <input
              className="snapped-call-form__input"
              type="url"
              name="press_links"
              placeholder="Press/Media Links (Optional)"
              value={formData.press_links}
              onChange={handleChange}
            />
          </div>

          {/* Spotlight Migration Section */}
          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">TikTok to Spotlight Migration</h3>
            
            <div style={{ 
              padding: '1.5rem', 
              backgroundColor: '#f0f7ff', 
              border: '2px solid #1976d2', 
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <p style={{ marginBottom: '1rem', fontSize: '1rem', lineHeight: '1.6' }}>
                <strong>Our team will help you migrate your TikTok video content to Spotlight.</strong>
              </p>
              <p style={{ marginBottom: '1rem', fontSize: '0.95rem', lineHeight: '1.6', color: '#555' }}>
                Spotlight is Snapchat's version of TikTok and is a monetization bucket we should be building.
              </p>
              <p style={{ marginBottom: '1.5rem', fontSize: '0.95rem', lineHeight: '1.6', color: '#555' }}>
                We move them over by downloading them without the TikTok logo, downloading the captions and data, and then posting them each day onto Spotlight on your behalf.
              </p>
              
              <div style={{ marginTop: '1rem' }}>
                <p style={{ marginBottom: '0.75rem', fontWeight: '600' }}>Would you like to opt in to this service?</p>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    cursor: 'pointer',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: spotlightOptIn === true ? '#e3f2fd' : '#fff',
                    border: spotlightOptIn === true ? '2px solid #1976d2' : '2px solid #ddd',
                    borderRadius: '6px',
                    fontWeight: spotlightOptIn === true ? '600' : '400'
                  }}>
                    <input
                      type="radio"
                      name="spotlight_opt_in"
                      checked={spotlightOptIn === true}
                      onChange={() => setSpotlightOptIn(true)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    Yes, opt me in
                  </label>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    cursor: 'pointer',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: spotlightOptIn === false ? '#ffebee' : '#fff',
                    border: spotlightOptIn === false ? '2px solid #d32f2f' : '2px solid #ddd',
                    borderRadius: '6px',
                    fontWeight: spotlightOptIn === false ? '600' : '400'
                  }}>
                    <input
                      type="radio"
                      name="spotlight_opt_in"
                      checked={spotlightOptIn === false}
                      onChange={() => setSpotlightOptIn(false)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    No thanks
                  </label>
                </div>
                
                {/* TikTok Username - Required if opted in */}
                {spotlightOptIn === true && (
                  <div style={{ marginTop: '1rem' }}>
                    <input
                      className={`snapped-call-form__input ${invalidFields.includes('spotlight_tiktok_username') ? 'snapped-call-form__input--error' : ''}`}
                      type="text"
                      placeholder="TikTok Username (required) *"
                      value={spotlightTiktokUsername}
                      onChange={(e) => setSpotlightTiktokUsername(e.target.value)}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    <small style={{ color: '#666' }}>Enter your TikTok username so we can migrate your content</small>
                    {invalidFields.includes('spotlight_tiktok_username') && (
                      <div className="snapped-call-form__error-message">TikTok username is required when opting in to Spotlight migration</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Admin Access Section */}
          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">Add Us as Admin on Snapchat</h3>
            
            <div style={{ 
              padding: '1.5rem', 
              backgroundColor: '#fff8e1', 
              border: '2px solid #ffc107', 
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <p style={{ marginBottom: '1rem', fontSize: '1rem', lineHeight: '1.6' }}>
                <strong>To manage your content, we need admin access to your Snapchat account.</strong>
              </p>
              
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#fff', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                border: '1px solid #e0e0e0'
              }}>
                <p style={{ marginBottom: '0.75rem', fontWeight: '600' }}>How to add us as an Admin:</p>
                <ol style={{ paddingLeft: '1.25rem', marginBottom: '0', lineHeight: '1.8' }}>
                  <li>Open Snapchat and go to your Profile</li>
                  <li>Tap the ⚙️ Settings icon</li>
                  <li>Tap "Manage My Public Profile"</li>
                  <li>Tap "Add or Remove Roles"</li>
                  <li>Search for and add ONE of the accounts below</li>
                </ol>
              </div>
              
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                border: '1px solid #1976d2'
              }}>
                <p style={{ marginBottom: '0.75rem', fontWeight: '600' }}>Add ONE of these accounts:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <code style={{ padding: '0.5rem', backgroundColor: '#fff', borderRadius: '4px', fontWeight: '600' }}>pixeltalent1</code>
                  <code style={{ padding: '0.5rem', backgroundColor: '#fff', borderRadius: '4px', fontWeight: '600' }}>pixeltalent2</code>
                  <code style={{ padding: '0.5rem', backgroundColor: '#fff', borderRadius: '4px', fontWeight: '600' }}>pixeltalent3</code>
                  <code style={{ padding: '0.5rem', backgroundColor: '#fff', borderRadius: '4px', fontWeight: '600' }}>pixeltalent04</code>
                  <code style={{ padding: '0.5rem', backgroundColor: '#fff', borderRadius: '4px', fontWeight: '600' }}>pixeltalent5</code>
                </div>
              </div>
              
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#ffebee', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                border: '1px solid #f44336'
              }}>
                <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.6' }}>
                  <strong>⚠️ Note:</strong> This feature can be finicky. If one account doesn't work, please try another from the list. 
                  If none of them work, select "Not Available" and let us know.
                </p>
                <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.9rem', lineHeight: '1.6' }}>
                  <strong>📱 New Account?</strong> If you recently created your Snapchat account and are not at least verified, 
                  you may not have access to add admins yet. Select "Not Available" and we can try again later.
                </p>
              </div>
              
              <div style={{ marginTop: '1rem' }}>
                <p style={{ marginBottom: '0.75rem', fontWeight: '600' }}>Were you able to add us?</p>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    cursor: 'pointer',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: adminAdded === true ? '#e8f5e9' : '#fff',
                    border: adminAdded === true ? '2px solid #4caf50' : '2px solid #ddd',
                    borderRadius: '6px',
                    fontWeight: adminAdded === true ? '600' : '400'
                  }}>
                    <input
                      type="radio"
                      name="admin_added"
                      checked={adminAdded === true}
                      onChange={() => setAdminAdded(true)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    I Added You
                  </label>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    cursor: 'pointer',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: adminAdded === false ? '#fff3e0' : '#fff',
                    border: adminAdded === false ? '2px solid #ff9800' : '2px solid #ddd',
                    borderRadius: '6px',
                    fontWeight: adminAdded === false ? '600' : '400'
                  }}>
                    <input
                      type="radio"
                      name="admin_added"
                      checked={adminAdded === false}
                      onChange={() => setAdminAdded(false)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    Not Available
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Dynamic Terms of Service based on split selection */}
          {renderTermsOfService()}

          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">Identity Verification</h3>
            <div className="snapped-call-form__helper-text" style={{ marginBottom: '1rem' }}>
              <p><strong>Please upload a video saying the following:</strong></p>
              <p style={{ 
                fontStyle: 'italic', 
                padding: '1rem', 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #dee2e6', 
                borderRadius: '4px',
                margin: '1rem 0'
              }}>
                "My name is [NAME] and I am submitting this video to verify my identity and confirm my request to be onboarded into Snapchat's creator programs. I acknowledge that Snapchat does not charge fees or accept payment for program invitations, and that any third party offering such access in exchange for payment is not authorized to act on Snapchat's behalf. I further confirm my understanding and consent for Peak Content to facilitate my onboarding into Snapchat's creator programs on my behalf."
              </p>
            </div>
            
            <div className="snapped-call-form__dropzone" onClick={() => document.getElementById('verification-video-input').click()}>
              <input
                id="verification-video-input"
                type="file"
                accept="video/*"
                onChange={(e) => handleFileChange(e, 'verification_video')}
                style={{ display: 'none' }}
                {...(!isEditing && { required: true })}
              />
              <div className="snapped-call-form__dropzone-content">
                <div className="snapped-call-form__upload-icon">🎥</div>
                <p className="snapped-call-form__dropzone-text">
                  {formData.verification_video 
                    ? `✓ ${formData.verification_video.name}`
                    : 'Click to upload video or drag and drop'
                  }
                </p>
              </div>
            </div>
            <p className="snapped-call-form__helper-text">
              Max file size: 100MB. Supported formats: MP4, MOV, AVI, MKV, WebM
            </p>
            {invalidFields.includes('verification_video') && (
              <span className="snapped-call-form__error-message">Verification video is required</span>
            )}
            
            {existingResponse && existingResponse.verification_video_filename && (
              <div style={{ 
                padding: '0.75rem', 
                backgroundColor: '#e8f4f8', 
                border: '1px solid #bee5eb', 
                borderRadius: '4px', 
                marginTop: '0.5rem' 
              }}>
                <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: '#0c5460' }}>
                  Current video: {existingResponse.verification_video_filename}
                </p>
                <small style={{ color: '#0c5460' }}>Upload a new video to replace the current one</small>
              </div>
            )}
          </div>
          
          {uploadStatus && (
            <div style={{
              padding: '1rem',
              backgroundColor: uploadStatus.includes('✓') ? '#d4edda' : '#cce5ff',
              border: `1px solid ${uploadStatus.includes('✓') ? '#c3e6cb' : '#b8daff'}`,
              borderRadius: '4px',
              marginBottom: '1rem',
              color: uploadStatus.includes('✓') ? '#155724' : '#004085',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              {uploadStatus}
            </div>
          )}

          <button 
            type="submit" 
            className="snapped-call-form__submit-button"
            disabled={submitting}
          >
            {submitting ? uploadStatus || 'Submitting...' : (isEditing ? 'Update Form' : 'Submit Form')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PixelMonetizationForm;

